/**
 * @fileoverview Pure per-item merge for embedded building systems — the
 *               single canonical merger shared by migration upserts and all
 *               client/server conflict seams.
 * @module sync-config/embeddedSystemMerge
 *
 * Responsibilities:
 * - Merge two copies of one embedded `inspectableEntities.systems[]` record
 *   keyed by the same `systemId`, preserving fields owned by either side.
 * - Union embedded child arrays (events/tests/history/attachments/childItems
 *   slots) by stable identity keys so concurrent or re-run additions from
 *   either side survive.
 * - Enforce tombstone dominance: once a system is `_deleted`, no merge
 *   resurrects it (G-009).
 *
 * Dependencies:
 * - None — pure record manipulation; callers supply their own shapes
 *   (wire `EmbeddedBuildingSystem`, Mongo loader rows, or Dexie snapshots).
 *
 * Architecture:
 * - Directionality: `incoming` wins scalar/spec keys it defines; `existing`
 *   contributes keys `incoming` lacks. For migration, existing=stored Mongo
 *   record and incoming=fresh legacy row. For client conflict resolution,
 *   existing=local Dexie copy and incoming=server copy (server-authoritative
 *   scalars, local-only fields preserved).
 * - Two type families exist for the stored record — the wire contract in
 *   `embeddedBuildingSystem.ts` (ISO strings) and the loader's Mongo-native
 *   shape (`Date` fields). This helper accepts loose records and never
 *   coerces date types, so it is neutral to both.
 *
 * Notes:
 * - `preserveSpecKeys` lets a caller pin existing spec values that must
 *   survive a rerun (e.g. assigned-CRR event history markers).
 * - Keyless child items are deduplicated by full-record equality so merged
 *   arrays never grow on repeated identical merges.
 */

/** Identity keys probed in order when union-merging embedded child items. */
const CHILD_ID_KEYS = [
  'id',
  'eventId',
  'testId',
  '_id',
  'blobName',
  'serialNumber',
  'panelId',
  'detectorId',
  'keyBoxId',
  'chemicalId',
  'tankId',
  'equipmentId',
  'alarmId',
  'pumpId',
  'linkId',
  'permitId',
  'childId',
];

export interface EmbeddedSystemMergeOptions {
  /** Value written to `updatedAt` on the merged record (ISO string or Date). */
  clock?: Date | string;
  /** Spec keys always taken from `existing` when present there. */
  preserveSpecKeys?: readonly string[];
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? (value as Array<Record<string, unknown>>) : [];
}

function itemKey(item: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const v = item[key];
    if (v != null && v !== '') return `${key}:${String(v)}`;
  }
  return undefined;
}

/**
 * Union two embedded child arrays. Items sharing an identity key keep the
 * `existing` copy (its local/user edits win); `incoming`-only items append.
 * Keyless items deduplicate by serialized content.
 */
function unionByIdentity(
  existing: Array<Record<string, unknown>>,
  incoming: Array<Record<string, unknown>>,
  keys: readonly string[] = CHILD_ID_KEYS,
): Array<Record<string, unknown>> {
  const merged = [...existing];
  const seen = new Set<string>();
  for (const item of existing) {
    seen.add(itemKey(item, keys) ?? `json:${JSON.stringify(item)}`);
  }
  for (const item of incoming) {
    const k = itemKey(item, keys) ?? `json:${JSON.stringify(item)}`;
    if (!seen.has(k)) {
      merged.push(item);
      seen.add(k);
    }
  }
  return merged;
}

/**
 * @process mergeEmbeddedBuildingSystem
 * Steps: spread existing->incoming scalars; merge spec with preserve-keys;
 *   union child arrays by identity; merge childItems slots; OR tombstones;
 *   bump version; stamp updatedAt.
 * Inputs: two records for the same systemId + options.
 * Outputs: merged record (new object; inputs untouched).
 * Failure Handling: missing/malformed arrays degrade to empty unions.
 * Idempotency: REQUIRED — merging the same pair twice yields the same content
 *   (version bumps again; array members do not duplicate).
 */
export function mergeEmbeddedBuildingSystem(
  existing: Record<string, unknown> | undefined,
  incoming: Record<string, unknown>,
  options: EmbeddedSystemMergeOptions = {},
): Record<string, unknown> {
  const base = asRecord(existing);
  const inc = asRecord(incoming);
  const preserveKeys = options.preserveSpecKeys ?? [];

  const existingSpec = asRecord(base.spec);
  const incomingSpec = asRecord(inc.spec);
  const preservedSpec = Object.fromEntries(
    preserveKeys
      .filter((key) => existingSpec[key] !== undefined)
      .map((key) => [key, existingSpec[key]]),
  );

  const existingChildren = asRecord(base.childItems);
  const incomingChildren = asRecord(inc.childItems);
  const mergedChildren: Record<string, unknown> = { ...existingChildren, ...incomingChildren };
  for (const slot of new Set([...Object.keys(existingChildren), ...Object.keys(incomingChildren)])) {
    if (Array.isArray(existingChildren[slot]) || Array.isArray(incomingChildren[slot])) {
      mergedChildren[slot] = unionByIdentity(
        asArray(existingChildren[slot]),
        asArray(incomingChildren[slot]),
      );
    }
  }

  const isDeleted = base._deleted === true || inc._deleted === true;
  const deletedAt = base.deletedAt ?? inc.deletedAt;
  const deletedBy = base.deletedBy ?? inc.deletedBy;

  const existingVersion = typeof base.version === 'number' ? base.version : 0;
  const incomingVersion = typeof inc.version === 'number' ? inc.version : 0;
  const existingUpdated = Date.parse(String(base.updatedAt ?? ''));
  const incomingUpdated = Date.parse(String(inc.updatedAt ?? ''));
  const existingIsNewer = Number.isFinite(existingUpdated)
    && Number.isFinite(incomingUpdated)
    && existingUpdated > incomingUpdated;
  const scalarWinner = existingVersion > incomingVersion || existingIsNewer ? base : inc;
  const scalarOther = scalarWinner === base ? inc : base;

  const merged: Record<string, unknown> = {
    ...scalarOther,
    ...scalarWinner,
    spec: { ...existingSpec, ...incomingSpec, ...preservedSpec },
    createdAt: base.createdAt ?? inc.createdAt,
    updatedAt: options.clock ?? inc.updatedAt ?? base.updatedAt,
    version: Math.max(existingVersion, incomingVersion) + 1,
  };
  if (isDeleted) {
    merged._deleted = true;
    if (deletedAt !== undefined) merged.deletedAt = deletedAt;
    else delete merged.deletedAt;
    if (deletedBy !== undefined) merged.deletedBy = deletedBy;
    else delete merged.deletedBy;
  } else {
    delete merged._deleted;
    delete merged.deletedAt;
    delete merged.deletedBy;
  }
  for (const key of Object.keys(merged)) {
    if (merged[key] === undefined) delete merged[key];
  }
  if (merged.spec && typeof merged.spec === 'object') {
    const specRecord = merged.spec as Record<string, unknown>;
    for (const key of Object.keys(specRecord)) {
      if (specRecord[key] === undefined) delete specRecord[key];
    }
  }
  // Absent stays absent — empty unions are not materialized onto records
  // that never carried the array.
  const mergeArraySlot = (key: string, keys: readonly string[]) => {
    if (base[key] !== undefined || inc[key] !== undefined) {
      merged[key] = unionByIdentity(asArray(base[key]), asArray(inc[key]), keys);
    }
  };
  mergeArraySlot('events', ['eventId', 'id', '_id']);
  mergeArraySlot('tests', ['testId', 'id', '_id']);
  mergeArraySlot('history', ['testId', 'id', '_id']);
  mergeArraySlot('attachments', ['_id', 'blobName', 'id']);
  if (Object.keys(mergedChildren).length > 0) {
    merged.childItems = mergedChildren;
  } else {
    delete merged.childItems;
  }
  return merged;
}
